'use client'

import { JSX } from 'react'

import { capitalize } from 'lodash'
import {
  ArrowLeftRight,
  BookOpen,
  CreditCard,
  FileChartColumn,
  Fuel,
  Layers,
  LifeBuoy,
  RefreshCw,
  Scale,
  Tags,
} from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useForm, useWatch } from 'react-hook-form'

import { PlanCheckBadge } from '~/app/settings/team/upgrade/PlanCheckBadge'
import { PlanDepositSection } from '~/app/settings/team/upgrade/PlanDepositSection'
import { PlanEarnProgress } from '~/app/settings/team/upgrade/PlanEarnProgress'
import {
  PreviewFormValues,
  ScenarioPreviewCard,
} from '~/app/settings/team/upgrade/ScenarioPreviewCard'
import { useUpgradeState } from '~/app/settings/team/upgrade/useUpgradeState'
import { formatUsdc } from '~/app/settings/team/upgrade/utils'
import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  Group,
  LabelValue,
  LoadingSpinner,
  PageLayout,
  SegmentedControl,
  Skeleton,
} from '~/components/component-library'
import { useActiveOrgMember } from '~/hooks/org'
import { useAutoEarnApy } from '~/hooks/useAutoEarnApy'
import { useOrgPlan } from '~/hooks/useOrgPlan'
import { PaidPlanName, usePlans } from '~/hooks/usePlans'
import { OrgMemberRole } from '~/types'
import { meetsPlan } from '~/utils/plans'

const PAID_PLAN_NAMES: PaidPlanName[] = ['pro', 'premium']

// What each plan adds over the tier below; mirrors splits.org/pricing.
const PLAN_PERKS: Record<
  PaidPlanName,
  { icon: JSX.Element; content: string; meta?: string }[]
> = {
  pro: [
    {
      icon: <BookOpen />,
      content: 'Cost basis tracking',
      meta: 'FIFO, LIFO, HIFO',
    },
    {
      icon: <FileChartColumn />,
      content: 'Gain/loss and transaction history reports',
      meta: 'PDF, CSV',
    },
    { icon: <RefreshCw />, content: 'Invoice and transaction schedules' },
    { icon: <CreditCard />, content: 'Rain card monitoring' },
    { icon: <Fuel />, content: 'Gas sponsorship', meta: '$10/mo' },
    { icon: <ArrowLeftRight />, content: 'Free on/offramp', meta: '$5,000/mo' },
    { icon: <LifeBuoy />, content: 'Support SLA', meta: '3 business days' },
  ],
  premium: [
    { icon: <Layers />, content: 'Everything in Pro' },
    { icon: <Tags />, content: 'Rules-based transaction categorization' },
    { icon: <RefreshCw />, content: 'Sync to QuickBooks and Xero' },
    { icon: <Scale />, content: 'Compliance' },
    { icon: <Fuel />, content: 'Gas sponsorship', meta: '$100/mo' },
    {
      icon: <ArrowLeftRight />,
      content: 'Free on/offramp',
      meta: '$50,000/mo',
    },
    { icon: <LifeBuoy />, content: 'Support SLA', meta: '1 business day' },
  ],
}

// Restates what an Earn balance holds under the selected tab: the rate it
// earns and the benefits it unlocked.
const getHeldCopy = ({
  planLabel,
  apy,
}: {
  planLabel: string
  apy?: number
}) =>
  apy
    ? `Your USDC is earning ${apy}% APY and unlocks ${planLabel}.`
    : `Your USDC in Earn unlocks ${planLabel}.`

// Names what is left to deposit. Leaves the APY clause out until the live rate
// is known.
const getEarnCopy = ({
  earnMinimum,
  amountNeeded,
  apy,
  planLabel,
}: {
  earnMinimum: number
  amountNeeded: number
  apy?: number
  planLabel: string
}) => {
  const amount = Math.min(amountNeeded, earnMinimum)
  const keepInEarn = `Deposit ${formatUsdc(amount)} USDC into Earn`
  return apy
    ? `${keepInEarn} to unlock ${planLabel} while earning ${apy}% APY.`
    : `${keepInEarn} to unlock ${planLabel}.`
}

type PlanFormValues = { plan: PaidPlanName }

const UpgradePageContent = ({ initialPlan }: { initialPlan: PaidPlanName }) => {
  const { control: planControl } = useForm<PlanFormValues>({
    defaultValues: { plan: initialPlan },
  })
  const planName = useWatch({ control: planControl, name: 'plan' })
  const { control: previewControl } = useForm<PreviewFormValues>({
    defaultValues: { scenario: 'live' },
  })
  const previewValue = useWatch({ control: previewControl, name: 'scenario' })
  const previewScenario =
    previewValue === 'live'
      ? undefined
      : previewValue === 'member'
        ? 'insufficient'
        : previewValue
  const isPreviewing = previewScenario !== undefined
  // Only owners can create the deposit automation.
  const orgMember = useActiveOrgMember()
  const isOwner =
    previewValue === 'member' ? false : orgMember?.role === OrgMemberRole.Owner

  const { data: orgPlan } = useOrgPlan()
  const { plans } = usePlans()
  const { apy } = useAutoEarnApy()
  const currentPlanName = orgPlan?.plan
  const label = capitalize(planName)
  const earnMinimum = plans.find((p) => p.name === planName)?.earnMinimum

  const upgradeState = useUpgradeState({ planName, previewScenario })
  const { scenario, amountNeeded, isEarnBacked, isLoading, errorMessage } =
    upgradeState
  // An unlocked plan keeps its bar, full and green, however it was unlocked.
  const showEarnProgress = !errorMessage && (isLoading || !!scenario)
  const isPlanUnlocked = (name: PaidPlanName) =>
    meetsPlan({ plans, current: currentPlanName, required: name })
  // Plans are ordered low to high, so the last unlocked one is the org's level.
  const unlockedPlanName = PAID_PLAN_NAMES.filter(isPlanUnlocked).at(-1)
  // Nothing needed means the tab's plan is held, by itself or by a higher one.
  // A plan held another way than Earn (subscription, override) gets no sentence.
  const earnCopy =
    amountNeeded === undefined || earnMinimum === undefined
      ? undefined
      : amountNeeded > 0
        ? getEarnCopy({ earnMinimum, amountNeeded, apy, planLabel: label })
        : isEarnBacked
          ? getHeldCopy({ planLabel: label, apy })
          : undefined

  return (
    <PageLayout
      title="Upgrade Splits"
      actionButtons={
        <SegmentedControl
          control={planControl}
          inputName="plan"
          ariaLabel="Choose your plan"
          options={PAID_PLAN_NAMES.map((name) => ({
            value: name,
            label: capitalize(name),
            // Marks the highest plan the org holds.
            icon:
              name === unlockedPlanName ? (
                <PlanCheckBadge isSelected={planName === name} />
              ) : undefined,
          }))}
        />
      }
    >
      <div className="space-y-8">
        {earnMinimum !== undefined && (
          <div className="space-y-3">
            {isLoading ? (
              <Skeleton length={24} height={0.875} />
            ) : (
              earnCopy && <p className="text-sm text-secondary">{earnCopy}</p>
            )}
            {showEarnProgress && (
              <PlanEarnProgress
                earnMinimum={earnMinimum}
                amountNeeded={amountNeeded}
                planLabel={label}
                isLoading={isLoading}
              />
            )}
          </div>
        )}
        <Group label={`${label} includes`}>
          {PLAN_PERKS[planName].map((perk, index) => (
            <LabelValue
              key={perk.content}
              icon={perk.icon}
              label={perk.content}
              value={perk.meta ?? ''}
              listIndex={index}
              size="small"
            />
          ))}
        </Group>
        <PlanDepositSection
          planName={planName}
          isPreviewing={isPreviewing}
          isOwner={isOwner}
          upgradeState={upgradeState}
        />
      </div>
      <ScenarioPreviewCard control={previewControl} />
    </PageLayout>
  )
}

const UpgradePageInner = () => {
  const searchParams = useSearchParams()
  const planParam = searchParams.get('plan')
  const { data: orgPlan, isPending } = useOrgPlan()

  // The plan form reads its default once, so wait for the org's plan first.
  if (isPending)
    return (
      <PageLayout title="Upgrade Splits">
        <div className="text-secondary">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )

  // Without a plan in the URL, offer the tier above the current one.
  const nextPlanName: PaidPlanName =
    orgPlan?.plan === 'pro' || orgPlan?.plan === 'premium' ? 'premium' : 'pro'
  const initialPlan: PaidPlanName =
    PAID_PLAN_NAMES.find((name) => name === planParam) ?? nextPlanName

  return <UpgradePageContent initialPlan={initialPlan} />
}

export const TeamUpgradePage = withAuthentication(UpgradePageInner)
